import { useCallback, useEffect, useRef } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useManageUrgencyLevel } from 'src/graphql/entities/signal-business-hours/hooks/use-manage-urgency-level';
import { NormalizedSupportPolicy } from 'src/graphql/entities/support-policy/support-policy.utils';
import { buildDefaultEscalationPolicy, getEnabledUrgencyLevels } from './escalation-defaults';
import { POLICY_CATEGORY_ESCALATION } from './policy-constants';
import { SupportPolicyFormValues } from './policy-form';

interface UseEnsureEscalationPoliciesOptions {
  partnerId: number | undefined;
  policies: NormalizedSupportPolicy[];
  isLoading: boolean;
  onCreate: (values: SupportPolicyFormValues) => void;
  enabled: boolean;
}

/**
 * Ensures that default escalation policies exist for each enabled urgency level.
 * If a partner is missing an escalation policy for an enabled urgency, it auto-creates one.
 *
 * Skips when partnerId is undefined (still loading) or 0 (platform partner) --
 * escalation policies are always partner-specific.
 */
export function useEnsureEscalationPolicies({
  partnerId,
  policies,
  isLoading,
  onCreate,
  enabled,
}: UseEnsureEscalationPoliciesOptions): void {
  const [disabledUrgencies] = useManageUrgencyLevel();
  const createdRef = useRef<Set<string>>(new Set());
  const lastPartnerIdRef = useRef<number | undefined>(undefined);

  // Reset tracking when partner changes
  if (lastPartnerIdRef.current !== partnerId) {
    createdRef.current = new Set();
    lastPartnerIdRef.current = partnerId;
  }

  const ensureDefaults = useCallback(() => {
    // skip entirely when the partner has not opted into new escalation policies
    if (!enabled) return;

    // wait until we have a real partner ID (not undefined, not platform)
    if (partnerId === undefined || partnerId === PLATFORM_PARTNER_ID || isLoading) return;

    const enabledUrgencies = getEnabledUrgencyLevels(disabledUrgencies);

    // only consider escalation policies that belong to this specific partner
    const existingUrgencies = new Set(
      policies
        .filter(
          (p) =>
            p.category === POLICY_CATEGORY_ESCALATION &&
            p.urgencyLevel &&
            p.partnerId === partnerId,
        )
        .map((p) => p.urgencyLevel),
    );

    for (const urgency of enabledUrgencies) {
      if (existingUrgencies.has(urgency) || createdRef.current.has(urgency)) {
        continue;
      }

      const defaults = buildDefaultEscalationPolicy(urgency);
      createdRef.current.add(urgency);

      onCreate({
        title: defaults.title,
        description: defaults.description,
        type: 'requirement',
        category: POLICY_CATEGORY_ESCALATION,
        pinnedAt: false,
        validUntil: null,
        userGroups: [],
        addressIds: [],
        clientIds: [],
        clientToAddressIds: [],
        enableConditions: false,
        conditions: { combinator: 'and', rules: [] },
        urgencyLevel: defaults.urgencyLevel,
        escalationRules: defaults.escalationRules,
      });
    }
  }, [enabled, partnerId, isLoading, policies, disabledUrgencies, onCreate]);

  useEffect(() => {
    ensureDefaults();
  }, [ensureDefaults]);
}
